import { Node, sp, warn } from 'cc';
import Delay from '../Utils/Delay';

enum EventType {
    SET_TRACK_TIME = 'SET_TRACK_TIME',
}

declare module 'cc' {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    namespace sp {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        interface Skeleton {
            // includes materials which sp.Skeleton actually uses
            materialCache: sp.Skeleton['_materialCache'];
            // current animation name
            skinName: string;
            /**
             * get all animations of the skeleton data
             */
            animations: sp.spine.Animation[];
            /**
             * track time of the default track (track 0 for non-cache mode, cached track for cache mode)
             */
            defaultTrackTime: number;
            /**
             * 取得指定動畫的表演時長
             * @param {string} animationName 動畫名稱
             */
            getDuration(animationName: string): number;
            /**
             * 接續在當前正在播放的動畫後播放，若當前動畫為Loop則等待本輪播放結束
             * @param {string} animationName 新的動畫名稱
             * @param {boolean} loop 新的動畫循環播放
             * @param {number} speedUp 當前動畫要加速幾倍至播放完畢
             */
            appendAnimation(animationName: string, loop: boolean, speedUp?: number): void;
            /**
             * clear animation queue and headAniInfo (only for cache mode)
             */
            clearAnimation(): void;
            /**
             * 更新綁定的 sockets(只能覆蓋原有的 sockets，不能新增)
             */
            updateSockets(sockets: sp.SpineSocket[]): void;
        }
    }
}

const originSetMix = sp.Skeleton.prototype.setMix;
sp.Skeleton.prototype.setMix = function setMix(this: sp.Skeleton, fromAnimation: string, toAnimation: string, duration: number) {
    if (this.node.activeInHierarchy) {
        originSetMix.call(this, fromAnimation, toAnimation, duration);
    } else {
        const cb = () => {
            if (this.node.activeInHierarchy) {
                try {
                    originSetMix.call(this, fromAnimation, toAnimation, duration);
                } catch (error) {
                    // ignore
                }
                this.node.off(Node.EventType.ACTIVE_IN_HIERARCHY_CHANGED, cb, this);
            }
        };
        this.node.on(Node.EventType.ACTIVE_IN_HIERARCHY_CHANGED, cb, this);
    }
};

sp.Skeleton.prototype.getDuration = function getDuration(this: sp.Skeleton, name: string) {
    const animation = this.findAnimation(name);
    if (!animation) {
        throw new Error(`Can't find animation ${name}`);
    }
    return animation.duration;
};

sp.Skeleton.prototype.appendAnimation = async function appendAnimation(this: sp.Skeleton, animationName: string, loop: boolean, speedUp: number = 1) {
    if (this.isAnimationCached()) {
        const oriAnimationName = this._animationName;
        if (!oriAnimationName) {
            this.setAnimation(0, animationName, loop);
            return;
        }
        const oriAnimation = this.findAnimation(oriAnimationName);
        if (oriAnimation) {
            const accTime = this._accTime;
            const oriTimeScale = this._timeScale;
            this.timeScale *= speedUp;
            const nextEndTime = (oriAnimation.duration - (accTime % oriAnimation.duration)) / this.timeScale;
            await Delay.resolve(nextEndTime * 1000, this);
            this.timeScale = oriTimeScale;
            this.setAnimation(0, animationName, loop);
        } else {
            this.setAnimation(0, animationName, loop);
        }
    } else {
        warn("'appendAnimation' can not be invoked in REALTIME mode.");
    }
};

sp.Skeleton.prototype.clearAnimation = function clearAnimation(this: sp.Skeleton) {
    if (this.isAnimationCached()) {
        this._animationQueue = [];
        this._headAniInfo = null;
    }
};

sp.Skeleton.prototype.updateSockets = function updateSockets(this: sp.Skeleton, sockets: sp.SpineSocket[]) {
    this.sockets = [];
    sockets.forEach((socket) => {
        this.sockets.push(new sp.SpineSocket(socket.path, socket.target));
    });
    // 沒有這行的話 不會生效
    this.sockets = [...this.sockets];
};

Object.defineProperty(sp.Skeleton.prototype, 'animations', {
    configurable: true,
    get: function get(this: sp.Skeleton) {
        return this.skeletonData.getRuntimeData().animations;
    },
});

Object.defineProperty(sp.Skeleton.prototype, 'skinName', {
    configurable: true,
    get: function get(this: sp.Skeleton) {
        return this._skeleton?.skin?.name ?? '';
    },
});

Object.defineProperty(sp.Skeleton.prototype, 'defaultTrackTime', {
    configurable: true,
    get: function get(this: sp.Skeleton) {
        if (this.isAnimationCached()) {
            return this._accTime;
        }
        return this.getCurrent(0).trackTime;
    },
    set(this: sp.Skeleton, value: number) {
        if (this.isAnimationCached()) {
            this._accTime = value;
        } else {
            this.getCurrent(0).trackTime = value;
        }
        this.emit(EventType.SET_TRACK_TIME, value);
    },
});

Object.defineProperty(sp.Skeleton.prototype, 'materialCache', {
    configurable: true,
    get: function get(this: sp.Skeleton) {
        return this._materialCache;
    },
    set(this: sp.Skeleton, value: sp.Skeleton['_materialCache']) {
        if (this._materialCache === value) {
            return;
        }
        this._materialCache = value;
    },
});

const originFunc = (sp.Skeleton.prototype as any)._cleanMaterialCache;
(sp.Skeleton.prototype as any)._cleanMaterialCache = function _onMaterialModified(this: sp.Skeleton) {
    originFunc.call(this);
    this.node.emit('onCleanMaterialCache');
};
